import * as React from "react";
import { useCallback } from "react";
import { FieldArray, Field, useFormikContext } from "formik";
import styled, { css } from "styled-components";
import { useTranslation } from "react-i18next";
import { useGetAssetLabels } from "security-app/hooks/useAssetsAPI";
import { Button, Input } from "components/bootstrap";
import { IconButton, Spinner } from "components/common";
import useFieldTypes from "data-lake/hooks/useFieldTypes";
import FieldSelect from "data-lake/components/FieldSelect";
import useInputOptions from "data-lake/hooks/useInputOptions";
import DataLakeFilterValueField, { FIELDS_WITH_OPTIONS } from "./DataLakeFilterValueField";
const FieldContainer = styled.div`
  flex-basis: 100%;
`;
const RemoveContainer = styled.div`
  display: flex;
  align-items: center;
  height: 34px;
`;
const List = styled.div(
  ({ theme }) => css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacings.xs};
    margin-bottom: ${theme.spacings.xs};
  `
);
const FilterGroup = styled.div(
  ({ theme }) => css`
    flex-grow: 1;
    display: flex;
    gap: ${theme.spacings.xxs};
  `
);
const Item = styled.div(
  ({ theme }) => css`
    display: flex;
    gap: ${theme.spacings.xxs};
  `
);
const FieldFiltersConfiguration = ({ maxFilters = void 0 }) => {
  const { t } = useTranslation("enterprise_data_lake");
  const {
    values: { fieldFilters: filters },
    setFieldValue
  } = useFormikContext();
  const { data: filterableFields, isLoading: isLoadingFilterableField } = useFieldTypes();
  const { assetLabels, initialLoadingAssets: isLoadingAssets } = useGetAssetLabels({ page: 1, perPage: 0 });
  const inputOptions = useInputOptions();
  const isEmpty = (filters != null ? filters : []).length === 0;
  const handleRemove = useCallback(
    (index) => {
      setFieldValue(
        "fieldFilters",
        filters.filter((_, i) => i !== index)
      );
    },
    [filters, setFieldValue]
  );
  return /* @__PURE__ */ React.createElement(
    FieldArray,
    {
      name: "fieldFilters",
      validateOnChange: false,
      render: ({ push }) => /* @__PURE__ */ React.createElement(React.Fragment, null, isLoadingFilterableField && /* @__PURE__ */ React.createElement(Spinner, null), !isEmpty && !isLoadingFilterableField && !isLoadingAssets && /* @__PURE__ */ React.createElement(List, null, filters.map((filter, index) => (
        /* eslint-disable-next-line react/no-array-index-key */
        /* @__PURE__ */ React.createElement(Item, { key: `filter-field-${index}` }, /* @__PURE__ */ React.createElement(FilterGroup, null, /* @__PURE__ */ React.createElement(Field, { name: `fieldFilters.${index}.field_name`, required: true }, ({ field: { name, value }, meta: { error } }) => /* @__PURE__ */ React.createElement(FieldContainer, null, /* @__PURE__ */ React.createElement(Input, { error, formGroupClassName: "", id: `field_name-${index}` }, /* @__PURE__ */ React.createElement(
          FieldSelect,
          {
            value,
            isLoading: isLoadingFilterableField,
            id: name,
            name,
            size: "normal",
            onChange: (newVal) => {
              if (newVal !== value && (FIELDS_WITH_OPTIONS.includes(newVal) || FIELDS_WITH_OPTIONS.includes(value))) {
                setFieldValue(`fieldFilters.${index}.value`, "");
              }
              setFieldValue(`fieldFilters.${index}.field_name`, newVal);
            },
            placeholder: t("field_filters.field_name")
          }
        )))), /* @__PURE__ */ React.createElement(
          DataLakeFilterValueField,
          {
            fieldName: `fieldFilters.${index}.value`,
            field: filterableFields.find((field) => field.name === filter.field_name),
            assetLabels,
            inputOptions
          }
        ), /* @__PURE__ */ React.createElement(RemoveContainer, null, filters.length > 0 && /* @__PURE__ */ React.createElement(
          IconButton,
          {
            name: "delete",
            onClick: () => handleRemove(index),
            title: t("field_filters.remove_field")
          }
        ))))
      ))), /* @__PURE__ */ React.createElement(
        Button,
        {
          disabled: maxFilters && (filters == null ? void 0 : filters.length) >= maxFilters,
          bsSize: "xs",
          onClick: () => push({ field_name: "", value: "" }),
          name: "plus",
          title: t("field_filters.add_field_filter")
        },
        t("field_filters.add_field")
      ))
    }
  );
};
export default FieldFiltersConfiguration;
